// Feedback types
export type FeedbackType = 'bug_report' | 'feature_request' | 'general_feedback' | 'error_report';
export type FeedbackStatus = 'submitted' | 'under_review' | 'planned' | 'in_progress' | 'completed' | 'wont_fix';
export type FeedbackPriority = 'critical' | 'high' | 'medium' | 'low';
export type FeedbackSeverity = 'blocker' | 'major' | 'minor' | 'trivial';
export type AttachmentType = 'image' | 'video' | 'file' | 'screenshot' | 'recording' | 'audio';

// Entities
export interface IFeedbackSubmission {
	id: number;
	submitterId: string;
	type: FeedbackType;
	title: string;
	description: string;
	status: FeedbackStatus;
	priority: FeedbackPriority;
	severity: FeedbackSeverity | null;
	pageUrl: string | null;
	browserInfo: string | null;
	viewportSize: string | null;
	userRole: string | null;
	consoleErrors: string | null;
	metadata: string | null;
	assignedToId: string | null;
	voteCount: number;
	mergedIntoId: number | null;
	adminResponse: string | null;
	adminRespondedAt: string | null;
	resolvedAt: string | null;
	emuDeeTaskId: string | null;
	emuDeeDisplayId: string | null;
	emuDeeTaskUrl: string | null;
	emuDeeSyncedAt: string | null;
	createdAt: string;
	updatedAt: string;
	isDeleted: boolean;
}

export type EmuDeeItemType = 'epic' | 'story' | 'task';

export interface IFeedbackAttachment {
	id: number;
	feedbackSubmissionId: number | null;
	filename: string;
	originalFilename: string;
	mimeType: string;
	size: number;
	url: string;
	type: AttachmentType;
	storagePublicId: string | null;
	thumbnailUrl: string | null;
	width: number | null;
	height: number | null;
	duration: number | null;
	resourceType: string | null;
	createdAt: string;
}

export interface IFeedbackComment {
	id: number;
	feedbackSubmissionId: number;
	authorId: string;
	content: string;
	isInternal: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface IFeedbackVote {
	id: number;
	feedbackSubmissionId: number;
	userId: string;
	createdAt: string;
}

// Request DTOs
export interface ICreateFeedbackRequest {
	type: FeedbackType;
	title: string;
	description: string;
	priority?: FeedbackPriority;
	severity?: FeedbackSeverity;
	pageUrl?: string;
	browserInfo?: string;
	viewportSize?: string;
	consoleErrors?: string[];
	metadata?: Record<string, unknown>;
	attachmentIds?: number[];
}

export interface IUpdateFeedbackRequest {
	priority?: FeedbackPriority;
	severity?: FeedbackSeverity;
	assignedToId?: string;
	adminResponse?: string;
}

export interface IAddCommentRequest {
	content: string;
	isInternal: boolean;
}

export interface IFeedbackQueryParams {
	type?: FeedbackType;
	status?: FeedbackStatus;
	priority?: FeedbackPriority;
	search?: string;
	assignedToId?: string;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'ASC' | 'DESC';
}

export interface IBulkActionRequest {
	action: 'status' | 'assign';
	feedbackIds: number[];
	status?: FeedbackStatus;
	assignedToId?: string;
}

export interface ICreateEmuDeeTaskRequest {
	title?: string;
	description?: string;
	itemType?: EmuDeeItemType;
	priority?: FeedbackPriority;
	force?: boolean;
}

export interface IEmuDeeTaskResult {
	id: string;
	displayId: string | null;
	title: string;
	itemType: string;
	priority: string | null;
	status: string | null;
	createdAt: string | null;
	url: string | null;
}

// Response DTOs
export interface IPagedResponse<T> {
	items: T[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

export interface IVoteResult {
	voteCount: number;
	error?: string;
}

export interface IVotableFeedback {
	feedback: IFeedbackSubmission;
	hasVoted: boolean;
}

export interface IFeedbackDetail {
	feedback: IFeedbackSubmission;
	attachments: IFeedbackAttachment[];
	comments: IFeedbackComment[];
}

export interface IFeedbackStats {
	total: number;
	byType: Record<string, number>;
	byStatus: Record<string, number>;
	byPriority: Record<string, number>;
	avgResponseTimeHours: number | null;
}

export interface IBulkActionResult {
	updated: number;
}

// UI constants
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
	bug_report: 'Bug Report',
	feature_request: 'Feature Request',
	general_feedback: 'General Feedback',
	error_report: 'Error Report',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
	submitted: 'Submitted',
	under_review: 'Under Review',
	planned: 'Planned',
	in_progress: 'In Progress',
	completed: 'Completed',
	wont_fix: "Won't Fix",
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
	critical: 'Critical',
	high: 'High',
	medium: 'Medium',
	low: 'Low',
};

export const FEEDBACK_SEVERITY_LABELS: Record<FeedbackSeverity, string> = {
	blocker: 'Blocker',
	major: 'Major',
	minor: 'Minor',
	trivial: 'Trivial',
};

export const FEEDBACK_STATUS_COLORS: Record<FeedbackStatus, string> = {
	submitted: 'var(--text-secondary)',
	under_review: 'var(--color-info)',
	planned: '#7c3aed',
	in_progress: 'var(--color-warning)',
	completed: 'var(--color-success)',
	wont_fix: 'var(--color-danger)',
};

export const FEEDBACK_PRIORITY_COLORS: Record<FeedbackPriority, string> = {
	critical: 'var(--color-danger)',
	high: 'var(--color-warning)',
	medium: 'var(--color-info)',
	low: 'var(--text-tertiary)',
};

export const FEEDBACK_TYPE_COLORS: Record<FeedbackType, string> = {
	bug_report: 'var(--color-danger)',
	feature_request: 'var(--color-info)',
	general_feedback: 'var(--text-secondary)',
	error_report: 'var(--color-warning)',
};
